import * as Model from '../models';

import { AppConfigService } from './app-config-service';
import { UserDetails, UserDetailsService } from './user-details-service';

import { isIE } from '../components/common';

// Let's try to limit the size of the message we send over
// to teams cause it's possible we hit some limits:
// e.g. Webhook message delivery failed with error: Microsoft Teams endpoint returned HTTP error 413 with
const TEAMS_PAYLOAD_LIMIT: number = 18500;

/**
 * Since the message string is sent over to teams as JSON, we want to make
 * sure that any double quotes are replaced and the entire message fits within the limit
 * @param message The raw message to send over
 */
const prepareMessage: (message: string) => string = (message: string): string => {
  return message.replace(/\"/g, `'`).substring(0, TEAMS_PAYLOAD_LIMIT);
};

type AlertType = {
  name: string;
  type: Model.AppAlertLevel;
  iconUrl: string;
  themeColor: string;
  priority: number;
};

const AlertTypes: { none: AlertType; info: AlertType; error: AlertType; } = {
  none: {
    name: 'None',
    type: 'none',
    iconUrl: '',
    themeColor: 'ffffff',
    priority: 3,
  },
  info: {
    name: 'Info',
    type: 'info',
    iconUrl: 'https://teamsnodesample.azurewebsites.net/static/img/image2.png',
    themeColor: '0076d7',
    priority: 1,
  },
  error: {
    name: 'Error',
    type: 'error',
    iconUrl: 'https://teamsnodesample.azurewebsites.net/static/img/image1.png',
    themeColor: 'e71316',
    priority: 2,
  },
};

export interface AlertService {
  info(message: string): ng.IPromise<void>;
  error(message: string): ng.IPromise<void>;
}

/**
 * This is an interesting hacked implementation of posting to
 * a Microsoft Teams webhook by using form post.
 *
 * To circumvent CORS limitations, this implementation uses
 * a form post using a text/plain encoding to make it appear
 * as if it's doing an HTTP post. Do note the hack by
 * using an input 'name' attribute as the actual content of the payload.
 *
 * The details are in this post: https://systemoverlord.com/2016/08/24/posting-json-with-an-html-form.html
 *
 * Also, to make this work with IE, since IE does not respect the form 'enctype' attribute,
 * we replace all spaces and double quotes with blanks and single quotes to circumvent
 * url encoding of this characters
 */
export class MSTeamsAlertService implements AlertService {

  private alertForm: HTMLFormElement;
  private alertInput: HTMLInputElement;
  private isIEBrowser: boolean = false;
  private deferred?: ng.IDeferred<void>;

  constructor(private $q: ng.IQService,
              private userService: UserDetailsService,
              private configService: AppConfigService) {
    const isIEBrowser: boolean = isIE();
    const alertForm: HTMLFormElement = document.createElement('form');
    alertForm.style.display = 'none';
    alertForm.className = 'app-alert-form';
    alertForm.method = 'POST';
    alertForm.enctype = 'text/plain';
    alertForm.target = 'app-alert-frame';
    const alertInput: HTMLInputElement = document.createElement('input');
    alertInput.type = 'text';
    alertInput.value = '"}';
    const alertFrame: HTMLIFrameElement = document.createElement('iframe');
    alertFrame.tabIndex = -1;
    alertFrame.name = 'app-alert-frame';
    alertFrame.addEventListener('load', () => {
      const { deferred } = this;
      if (!deferred) {
        return;
      }
      try {
        if (alertFrame.contentDocument) {
          const responseData = alertFrame.contentDocument.body.textContent;
          console.error(responseData);
        }
      } catch (e) {
        console.error(e);
      }
      deferred.resolve();
      return;
    });
    alertForm.appendChild(alertInput);
    alertForm.appendChild(alertFrame);
    document.body.appendChild(alertForm);
    this.alertForm = alertForm;
    this.alertInput = alertInput;
    this.isIEBrowser = isIEBrowser;
  }

  public info(message: string): ng.IPromise<void> {
    return this.post(AlertTypes.info, message);
  }

  public error(message: string): ng.IPromise<void> {
    return this.post(AlertTypes.error, message);
  }

  private post(type: AlertType, message: string): ng.IPromise<void> {
    const deferred: ng.IDeferred<void> = this.$q.defer();
    const doNothing: (e: object | string | Error | undefined) => void = (e) => {
      console.error(e);
      deferred.resolve();
      return;
    };
    this.userService.getUserDetails().then((user: UserDetails) => {
      this.configService.getAppContent('').then((worldData: Model.WorldData) => {
        const { clientConfig } = worldData;
        const { alertLevel, alertWebhookUrl } = clientConfig;
        const targetAlertType: AlertType = AlertTypes[alertLevel] || AlertTypes.error;
        if (!alertWebhookUrl) {
          deferred.resolve();
          return;
        }
        if (type.priority < targetAlertType.priority) {
          deferred.resolve();
          return;
        }
        try {
          this.alertForm.action = alertWebhookUrl;
          const hostName: string = window.location.hostname;
          const userAgent: string = window.navigator.userAgent;
          const { userName, customerType, sessionToken: ltTokenId, countryCode, langCode, cartSessionId } = user;
          const now: Date = new Date();
          const actualMessage: string = prepareMessage(message);
          let alertPayload: string = `{
            "@type": "MessageCard",
            "@context": "http://schema.org/extensions",
            "themeColor": "${type.themeColor}",
            "summary": "${type.name} on ${hostName}",
            "sections": [{
              "activityTitle": "${type.name} on ${hostName}",
              "activitySubtitle": "On Custom Standard Oligo",
              "activityImage": "${type.iconUrl}",
              "facts": [{
                "name": "Alert Date",
                "value": "${now.toString()}"
              }, {
                "name": "Alert Date UTC",
                "value": "${now.toUTCString()}"
              }, {
                "name": "Browser",
                "value": "${userAgent}"
              }, {
                "name": "User",
                "value": "${userName}"
              }, {
                "name": "User Type",
                "value": "${customerType}"
              }, {
                "name": "Country / Language",
                "value": "${countryCode} / ${langCode}"
              }, {
                "name": "LT Token Id",
                "value": "${ltTokenId}"
              }, {
                "name": "Cart Session Id",
                "value": "${cartSessionId}"
              }, {
                "name": "Domain",
                "value": "${hostName}"
              }, {
                "name": "Message",
                "value": "${actualMessage}"
              }],
              "markdown": true
            }],
            "trash": "
          `;
          if (this.isIEBrowser) {
            alertPayload = alertPayload
              .replace(/'/g, `\\'`)
              .replace(/"/g, `'`)
              .replace(/\s\s+/g, '');
            this.alertInput.value = `'}`;
          }
          this.alertInput.name = alertPayload;
          this.alertForm.submit();
          return;
        } catch (e) {
          console.error('Error publishing alerts');
          deferred.resolve();
          return;
        }
      }, doNothing);
    }, doNothing);
    this.deferred = deferred;
    return deferred.promise;
  }
}

export const ALERT_SERVICE_NAME = 'appAlertService';
export const AlertServiceFactory = ($q: ng.IQService,
                                    userService: UserDetailsService,
                                    configService: AppConfigService): AlertService => {
  return new MSTeamsAlertService($q, userService, configService);
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0-0!./src/main/script/service/alert-service.ts